import type { ExternalCart, HeadlessOptions } from '@iheartjane/cart';
import { trackError } from '@iheartjane/tracking';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import type { PropsWithChildren } from 'react';

type MaybeString = string | undefined;

interface HeadlessAppContextType {
  externalId?: string;
  headlessCheckoutPartnerId?: string;
  headlessPartnerName?: string;
  headlessStoreId?: string;
  loadHeadlessApp: (externalCart: ExternalCart) => void;
  options: HeadlessOptions;
}

const HeadlessAppContext = createContext<HeadlessAppContextType>({
  externalId: undefined,
  headlessCheckoutPartnerId: undefined,
  headlessPartnerName: undefined,
  headlessStoreId: undefined,
  loadHeadlessApp: () => null,
  options: {},
});

export const HeadlessAppProvider = ({ children }: PropsWithChildren) => {
  const [externalId, setExternalId] = useState<MaybeString>(undefined);
  const [headlessPartnerName, setHeadlessPartnerName] =
    useState<MaybeString>(undefined);
  const [headlessCheckoutPartnerId, setHeadlessCheckoutPartnerId] =
    useState<MaybeString>(undefined);
  const [headlessStoreId, setHeadlessStoreId] =
    useState<MaybeString>(undefined);
  const [options, setOptions] = useState<HeadlessOptions>({});

  const loadHeadlessApp = useCallback((externalCart: ExternalCart) => {
    const {
      headlessCheckoutPartnerId,
      headlessPartnerName,
      options = {},
      storeId,
      user,
    } = externalCart;

    if (options.disableGuestCheckout && !options.loginUrl) {
      trackError('HeadlessApp has guest checkout disabled and no loginUrl', {
        storeId: externalCart.storeId,
      });
    }

    if (headlessCheckoutPartnerId) {
      setHeadlessCheckoutPartnerId(headlessCheckoutPartnerId);
    }

    if (headlessPartnerName) {
      setHeadlessPartnerName(headlessPartnerName);
    }

    if (storeId) {
      setHeadlessStoreId(storeId);
    }

    if (user) {
      setExternalId(user.externalId);
    }

    if (options) {
      setOptions((prevValue) => ({ ...prevValue, ...options }));
    }
  }, []);

  const contextValue = useMemo(
    () => ({
      externalId,
      headlessPartnerName,
      headlessCheckoutPartnerId,
      headlessStoreId,
      loadHeadlessApp,
      options,
    }),
    [
      externalId,
      headlessPartnerName,
      headlessCheckoutPartnerId,
      headlessStoreId,
      loadHeadlessApp,
      options,
    ]
  );
  return (
    <HeadlessAppContext.Provider value={contextValue}>
      {children}
    </HeadlessAppContext.Provider>
  );
};

// Instead of using a "safe" hook, or checking for actual context values,
// we create this context with the default values above so it always returns something.
export const useHeadlessApp = () => useContext(HeadlessAppContext);
